import { Injectable } from '@angular/core';
import { environment } from '../environments/environment';
import { QuotaQuestion, QuotaQuestionForList } from '../models/quota-question';
import { Observable, throwError } from 'rxjs';
import { catchError, shareReplay } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { DecipherQuotaEditLog, DecipherQuotaUpdate, QuotaSplitOption, QuotaTableDto } from '../models/decipherQuota';
import {
  ClickCalculatorQuotaResponse,
  ClickCalculatorTargetingResult,
} from '../models/click-calculator-quota';
import { VanguardQuotaList } from '../models/vanguardQuota';
import { TrueIr, TrueIrManualFigures } from '../models/true-ir';

@Injectable({
  providedIn: 'root'
})
export class QuotaService {

baseUrl = environment.apiEndpoint + '/Quota/';

private questions$: Observable<QuotaQuestionForList[]> | null = null;
private quotaCache = new Map<string, Observable<any>>();

constructor(private http: HttpClient) {}

invalidateQuotaCache(id: string) {
  this.quotaCache.delete(id);
}

  ListQuestionsForAdmin(instance: string = '00000000-0000-0000-0000-000000000000'): Observable<QuotaQuestionForList[]> {
    return this.http.get<QuotaQuestionForList[]>(this.baseUrl  + 'questions/' + instance);
  }

  ListQuestionsForProject(): Observable<QuotaQuestionForList[]> {
    if (!this.questions$) {
      this.questions$ = this.http.get<QuotaQuestionForList[]>(this.baseUrl + 'questions').pipe(
        shareReplay(1),
        catchError(err => { this.questions$ = null; return throwError(() => err); })
      );
    }
    return this.questions$;
  }

  GetQuestion(id: string): Observable<QuotaQuestion> {
    return this.http.get<QuotaQuestion>(this.baseUrl + 'question/' + id);
  }

  AddQuestion(model: any): Observable<QuotaQuestion> {
    return this.http.post<QuotaQuestion>(this.baseUrl + 'question', model);
  }

  EditQuestion(model: any): Observable<QuotaQuestion> {
    return this.http.put<QuotaQuestion>(this.baseUrl + 'question', model);
  }

  DeleteQuestion(model: any) {
    return this.http.delete(this.baseUrl + 'question/' + model.id);
  }
  
  GetQuota(id): Observable<any> {
    if (this.quotaCache.has(id)) return this.quotaCache.get(id)!;
    const obs$ = this.http.get<any>(this.baseUrl + id + '/quota').pipe(
      shareReplay(1),
      catchError(err => { this.invalidateQuotaCache(id); return throwError(() => err); })
    );
    this.quotaCache.set(id, obs$);
    return obs$;
  }

  SaveQuota(id, quotaDetails) {
    this.invalidateQuotaCache(id);
    return this.http.post<any>(this.baseUrl + id + '/quota', quotaDetails);
  }

  DeleteQuotaGroup(id, groupId) {
    this.invalidateQuotaCache(id);
    return this.http.delete<any>(this.baseUrl + id + '/quotaGroup/' + groupId);
  }

  CloneQuotaFromProject(projectId, templateProjectId) {
    this.invalidateQuotaCache(projectId);
    return this.http.put<any>(this.baseUrl + projectId + '/quota/clone/' + templateProjectId, {});
  }

  SaveSingleQuota(projectId, quota, refreshParticipants) {
    this.invalidateQuotaCache(projectId);
    return this.http.post<any>(this.baseUrl + projectId + '/quota/' + quota.groupId + '/inline/' + refreshParticipants, quota);
  }

  SaveSingleVendorQuota(projectId, quotaDetailId, vendorQuota) {
    this.invalidateQuotaCache(projectId);
    return this.http.post<any>(this.baseUrl + projectId + '/quota/' + quotaDetailId + '/vendor/inline/', vendorQuota);
  }

  ImportQuotaFromFile(id, fileName, allocationType) {
    this.invalidateQuotaCache(id);
    return this.http.post<any>(this.baseUrl + id + '/quota/' + fileName + '/' + allocationType, {});
  }

  NestQuota(id, groupIds) {
    this.invalidateQuotaCache(id);
    return this.http.post<any>(this.baseUrl + id + '/quota/nest', groupIds);
  }

  UndoNest(id, groupId) {
    this.invalidateQuotaCache(id);
    return this.http.delete<any>(this.baseUrl + id + '/quota/nest/' + groupId);
  }

  CloneMissingQuotasFromORInstance(instanceId) {
    return this.http.post<any>(this.baseUrl + instanceId + '/clone', {});
  }

  GetDecipherQuotas(projectId) {
    return this.http.get<Array<QuotaTableDto>>(this.baseUrl + projectId + '/decipherquota');
  }

  /** NK-387. Read-only quota cells of the project's Vanguard study, overall quota first. */
  GetVanguardQuotas(projectId: string): Observable<VanguardQuotaList> {
    return this.http.get<VanguardQuotaList>(this.baseUrl + projectId + '/vanguardquota');
  }

  /**
   * Click Calculator quota data scoped to this project, plus Nav start and click totals. On a
   * shared Decipher survey, counts are attributed to this project via or1.
   */
  GetClickCalculatorQuotas(projectId: string, includePending = true): Observable<ClickCalculatorQuotaResponse> {
    return this.http.get<ClickCalculatorQuotaResponse>(this.baseUrl + projectId + '/clickcalculator' + (includePending ? '' : '?pending=false'));
  }

  /** Saves a manual targetable / non-targetable override for one quota table. */
  SetClickCalculatorTargeting(
    projectId: string,
    table: string,
    targetable: boolean
  ): Observable<ClickCalculatorTargetingResult> {
    return this.http.put<ClickCalculatorTargetingResult>(
      this.baseUrl + projectId + '/clickcalculator/targeting',
      { table, targetable }
    );
  }

  /** The project's True IR, its terms and their targeting (NK-502). Counted from Decipher on each read. */
  GetTrueIr(projectId: string): Observable<TrueIr> {
    return this.http.get<TrueIr>(this.baseUrl + projectId + '/clickcalculator/trueir');
  }

  /** Saves a targetable / non-targetable call on one term; returns True IR recounted. */
  SetTrueIrTermTargeting(projectId: string, value: string, targetable: boolean): Observable<TrueIr> {
    return this.http.put<TrueIr>(this.baseUrl + projectId + '/clickcalculator/trueir/terms', { value, targetable });
  }

  /** Confirms the terms as they stand, so True IR reads them. */
  ConfirmTrueIrTerms(projectId: string): Observable<TrueIr> {
    return this.http.post<TrueIr>(this.baseUrl + projectId + '/clickcalculator/trueir/terms/confirm', {});
  }

  /** Saves hand-entered True IR figures, which True IR then reads. */
  SaveManualTrueIr(projectId: string, figures: TrueIrManualFigures): Observable<TrueIr> {
    return this.http.put<TrueIr>(this.baseUrl + projectId + '/clickcalculator/trueir/manual', figures);
  }

  GetDecipherSplitCategories(projectId) {
    return this.http.get<Array<QuotaSplitOption>>(this.baseUrl + projectId + '/splitcategories');    
  }

  GetDecipherQuotaSplits(projectId, categories:Array<QuotaSplitOption>) {
    return this.http.post<Array<QuotaTableDto>>(this.baseUrl + projectId + '/decipherquotasplits', categories);    
  }

  /**
   * Saves a batch of quota limits and emits the rebuilt tables.
   *
   * One request for the whole batch: Decipher's quota/edit API takes a dictionary of
   * marker -> limit, so the API sends them together and either the batch applies or none
   * of it does. Every limit is validated server-side before anything is sent, so an
   * invalid row rejects the save rather than applying the rows ahead of it.
   */
  EditDecipherQuotas(models: Array<DecipherQuotaUpdate>, projectId): Observable<Array<QuotaTableDto>> {
    return this.http.put<Array<QuotaTableDto>>(this.baseUrl + projectId + '/decipherquota/edit', models);
  }

  AddEntryToDecipherQuotaLog(model: DecipherQuotaEditLog){
    return this.http.put<DecipherQuotaEditLog>(this.baseUrl + 'quotalog/edit', model);
  }
}
